import React, { useState, useEffect, useRef } from 'react';
import { 
  FormGroup,
  Label,
  Button,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  CodeInputContainer,
  CodeInput,
  ResendCodeButton,
  TimerText
} from './AuthStyles';
import authService from '../../services/authService';

const PhoneVerification = ({ phoneNumber, onSuccess }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Configurar o timer para reenvio do código
  useEffect(() => {
    let interval = null;
    
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
      clearInterval(interval);
    }
    
    return () => clearInterval(interval);
  }, [timer]);

  // Focar no primeiro input quando o componente é montado
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Permitir apenas números
    if (!/^\d*$/.test(value)) return;
    
    // Atualizar o código
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Limpar mensagens de erro
    if (error) setError('');
    
    // Mover para o próximo input se o atual estiver preenchido
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Mover para o input anterior ao pressionar backspace em um campo vazio
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerify = async () => {
    // Verificar se todos os campos estão preenchidos
    const allFilled = code.every(digit => digit !== '' && digit !== null && digit !== undefined);
    if (!allFilled) {
      // Não exibir mensagem de erro, apenas retornar
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Formatar o código
      const verificationCode = code.join('');
      
      // Verificar o código usando o serviço
      const response = await authService.verifyCode(phoneNumber, verificationCode);
      
      console.log('Resposta da API:', response);
      
      if (response === 'sucesso') {
        setSuccess('Código verificado com sucesso!');
        
        // Obter os dados do usuário que foram salvos pelo authService
        const userData = authService.getUserData();
        
        console.log("Dados do usuário:", userData);
        
        // Chamar a função de sucesso após um breve delay para mostrar a mensagem
        setTimeout(() => {
          onSuccess(userData);
        }, 1500);
      } else {
        setError('Código inválido. Por favor, verifique e tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao verificar código:', err);
      setError('Ocorreu um erro ao verificar o código. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    
    setIsLoading(true);
    setError('');
    setCanResend(false);
    
    try {
      // Reenviar o código usando o serviço
      const response = await authService.resendVerificationCode(phoneNumber);
      
      if (response === 'sucesso') {
        setSuccess('Código reenviado para seu WhatsApp!');
        setTimer(60); // Reiniciar o timer
      } else {
        setError('Não foi possível reenviar o código. Tente novamente mais tarde.');
        setCanResend(true);
      }
    } catch (err) {
      console.error('Erro ao reenviar código:', err);
      setError('Ocorreu um erro ao reenviar o código. Tente novamente mais tarde.');
      setCanResend(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Verificação</h2>
      <p style={{ textAlign: 'center', marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.8)' }}>
        Enviamos um código de 6 dígitos para o seu WhatsApp. Por favor, digite o código abaixo:
      </p>
      
      <CodeInputContainer>
        {code.map((digit, index) => (
          <CodeInput
            key={`code-input-${index}`}
            type="text"
            maxLength={1}
            value={digit || ''}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            ref={(el) => (inputRefs.current[index] = el)}
            className={digit ? 'filled' : ''}
            disabled={isLoading}
          />
        ))}
      </CodeInputContainer>
      
      {error && <ErrorMessage style={{ textAlign: 'center' }}>{error}</ErrorMessage>}
      {success && <SuccessMessage style={{ textAlign: 'center' }}>{success}</SuccessMessage>}
      
      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        {!canResend ? (
          <TimerText>Reenviar código em {timer}s</TimerText>
        ) : (
          <ResendCodeButton 
            onClick={handleResendCode}
            disabled={isLoading || !canResend}
          >
            Reenviar código
          </ResendCodeButton>
        )}
      </div>
      
      <Button 
        onClick={handleVerify} 
        disabled={isLoading || !code.every(digit => digit !== '' && digit !== null && digit !== undefined)}
        style={{ marginTop: '1.5rem' }}
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Verificando...
          </>
        ) : (
          'Verificar'
        )}
      </Button>
    </div>
  );
};

export default PhoneVerification;
